import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "How long does it take to sell a house in the UK?",
    a: "The average time from listing to completion in the UK is around 4-6 months. This includes 4-8 weeks to find a buyer, 2-4 weeks for offer negotiation and surveys, and 8-12 weeks for conveyancing and legal work. Properties with strong marketing (especially video) tend to sell faster.",
  },
  {
    q: "How much does it cost to sell a house?",
    a: "The main costs are: estate agent fees (1-1.5% + VAT of sale price), conveyancing (£800-1,500 + disbursements), EPC certificate (£60-120), and potentially removal costs (£500-2,000). There is no stamp duty for sellers. Budget around 2-3% of the sale price for total selling costs.",
  },
  {
    q: "Do I need a solicitor to sell my house?",
    a: "You need a licensed conveyancer or solicitor to handle the legal transfer of ownership. They manage the contract, title deeds, property searches, and completion. You can choose a solicitor or a licensed conveyancing firm. Online conveyancers tend to be cheaper but may offer less personal service.",
  },
  {
    q: "What happens if my buyer's chain collapses?",
    a: "If a chain collapse happens, your agent should have backup buyers from viewings. Properties with video marketing often generate more initial interest, giving the agent a deeper pool of potential buyers to fall back on. Your agent should keep marketing the property until exchange of contracts.",
  },
  {
    q: "Should I get my house valued by more than one agent?",
    a: "Yes, always get at least three valuations. Compare not just the price estimate but also the agent's marketing plan, their track record of achieved prices in your area, and the quality of their online listings. Look for agents who use video marketing, as these properties consistently attract more viewings.",
  },
]

/* ------------------------------------------------------------------ */
/* Timeline step                                                       */
/* ------------------------------------------------------------------ */

function TimelineStep({
  number,
  title,
  duration,
  children,
}: {
  number: string
  title: string
  duration: string
  children: React.ReactNode
}) {
  return (
    <div className="relative pl-10">
      {/* Connector line */}
      <div className="absolute left-[15px] top-8 h-full w-px bg-stone" />
      {/* Number circle */}
      <span className="absolute left-0 top-0 flex size-8 items-center justify-center rounded-full bg-red font-display text-sm font-bold text-white">
        {number}
      </span>
      <div className="pb-8">
        <h3 className="font-display text-lg font-bold text-charcoal">
          {title}
        </h3>
        <p className="mt-0.5 text-xs font-medium text-red">{duration}</p>
        <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate">
          {children}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function SellingYourHouseGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Selling Your House: Step-by-Step Guide UK 2026",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Sell Your House: Step by Step Guide UK 2026",
            description: meta.description,
            step: [
              { "@type": "HowToStep", name: "Choose an estate agent", text: "Get valuations from at least three local agents. Compare their marketing quality, track record, and fees." },
              { "@type": "HowToStep", name: "Prepare your property", text: "Declutter, deep clean, complete minor repairs, and tidy the garden." },
              { "@type": "HowToStep", name: "Set the right price", text: "Work with your agent to set a realistic asking price based on comparable evidence." },
              { "@type": "HowToStep", name: "Marketing and viewings", text: "Your agent creates photography, video, floorplans, and lists on portals." },
              { "@type": "HowToStep", name: "Accept an offer", text: "Your agent presents and negotiates offers on your behalf." },
              { "@type": "HowToStep", name: "Conveyancing", text: "Solicitors handle contracts, searches, and legal paperwork." },
              { "@type": "HowToStep", name: "Exchange and complete", text: "Exchange contracts, then complete on the agreed date." },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Selling Your House" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Selling your house
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            A complete step-by-step guide to selling your house in the UK.
            From choosing an agent and preparing your property to
            exchanging contracts and moving day.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Timeline content */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-8 font-display text-2xl font-bold text-charcoal">
            The selling process, step by step<span className="text-red">.</span>
          </h2>

          <TimelineStep number="1" title="Choose an estate agent" duration="Week 1">
            <p>
              Get valuations from at least three local agents. Don&rsquo;t
              just compare fees; compare the quality of their marketing.
              Look at their current listings on Rightmove: are the
              photographs professional? Do they use video? Is the
              description compelling?
            </p>
            <p>
              The best agents invest in professional photography, video
              tours, and social media marketing. Properties with video get
              2x more enquiries, so choosing an agent who offers video can
              make a real difference to how quickly you sell and the price
              you achieve.
            </p>
          </TimelineStep>

          <TimelineStep number="2" title="Prepare your property" duration="Weeks 1-2">
            <p>
              First impressions count. Declutter every room, deep clean
              the entire property, and complete minor repairs like
              dripping taps, scuffed paintwork, and squeaky hinges. Tidy
              the garden and make the entrance welcoming.
            </p>
            <p>
              Consider staging key rooms with neutral accessories, fresh
              flowers, and coordinated soft furnishings. The goal is to
              help buyers imagine themselves living there.
            </p>
          </TimelineStep>

          <TimelineStep number="3" title="Set the right price" duration="Week 2">
            <p>
              Your agent will recommend an asking price based on recent
              comparable sales, current market conditions, and the
              specific features of your property. A realistic price
              attracts more viewings and often leads to competitive offers.
            </p>
            <p>
              Be cautious of agents who suggest an inflated price to win
              your instruction. Overpriced properties sit on the market
              longer and often sell for less than they would have at the
              right price from the start.
            </p>
          </TimelineStep>

          <TimelineStep number="4" title="Marketing and viewings" duration="Weeks 2-8">
            <p>
              Your agent creates the marketing assets: professional
              photography, floorplans, property description, and
              (if they&rsquo;re smart) a video ad for social media.
              The property goes live on Rightmove, Zoopla, and the
              agent&rsquo;s website.
            </p>
            <p>
              Your agent arranges and conducts viewings, provides
              feedback, and qualifies interested buyers. Keep the
              property clean and tidy for viewings, and be flexible with
              timing. The more accessible the property, the more viewings
              you&rsquo;ll get.
            </p>
          </TimelineStep>

          <TimelineStep number="5" title="Receive and negotiate offers" duration="Weeks 4-10">
            <p>
              Your agent presents all offers to you. They should advise
              on the buyer&rsquo;s position: are they chain-free? Do
              they have a mortgage in principle? How quickly can they
              proceed?
            </p>
            <p>
              The highest offer isn&rsquo;t always the best. A
              chain-free cash buyer at £5,000 less may complete weeks
              faster than a buyer in a long chain. Your agent should help
              you weigh price against certainty and speed.
            </p>
          </TimelineStep>

          <TimelineStep number="6" title="Conveyancing" duration="Weeks 8-16">
            <p>
              Once you accept an offer, both sides instruct solicitors.
              The buyer&rsquo;s solicitor conducts property searches
              (local authority, environmental, drainage) and reviews
              the title. Your solicitor prepares the contract pack.
            </p>
            <p>
              This stage takes 8-12 weeks on average. Respond to your
              solicitor&rsquo;s queries promptly. Delays at this stage
              are the most common reason sales fall through.
            </p>
          </TimelineStep>

          <TimelineStep number="7" title="Exchange and completion" duration="Completion day">
            <p>
              At exchange, both parties sign contracts and the buyer pays
              a deposit (usually 10%). The sale is now legally binding
              and a completion date is set, typically 1-4 weeks later.
            </p>
            <p>
              On completion day, the remaining funds transfer, your
              solicitor confirms receipt, and you hand over the keys.
              The property is sold. Congratulations.
            </p>
          </TimelineStep>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "4-6 months", label: "average time from listing to completion" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "95%", label: "of buyers start their search online" },
            { stat: "3+", label: "agent valuations recommended before choosing" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Useful tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              { href: "/tools/stamp-duty-calculator", title: "Stamp duty calculator", desc: "Calculate SDLT on your next purchase." },
              { href: "/guides/estate-agent-fees", title: "Estate agent fees", desc: "How much do agents charge? Full guide." },
              { href: "/directory", title: "Find an agent", desc: "Browse and compare local estate agents." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Choose an agent who markets with video
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Properties with video get 2x more enquiries, leading to more
            viewings, more offers, and a better sale price. Find agents in
            your area who use professional video marketing.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/directory"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Browse estate agents
            </Link>
            <Link
              href="/areas"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Explore area guides
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
